import React, { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Calendar, DollarSign, Users, Clock, TrendingUp, MessageCircle } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/components/ui/use-toast';
import { SessionRequestsTab } from './tabs/SessionRequestsTab';
import { EarningsTab } from './tabs/EarningsTab';
import { CalendarTab } from './tabs/CalendarTab';
import { ClientsTab } from './tabs/ClientsTab';

interface TherapistStats {
  totalEarnings: number;
  upcomingSessions: number;
  completedSessions: number;
  averageRating: number;
  totalClients: number;
}

interface Session {
  id: string;
  client_name: string;
  scheduled_at: string;
  duration_minutes: number;
  session_type: 'online' | 'in_person';
  status: 'scheduled' | 'completed' | 'cancelled';
  price: number;
}

const mockSessions: Session[] = [
  {
    id: '1',
    client_name: 'Ana M.',
    scheduled_at: '2025-01-20T10:00:00Z',
    duration_minutes: 60,
    session_type: 'online',
    status: 'scheduled',
    price: 75
  },
  {
    id: '2',
    client_name: 'Carlos R.',
    scheduled_at: '2025-01-20T15:00:00Z',
    duration_minutes: 60,
    session_type: 'in_person',
    status: 'scheduled',
    price: 95
  }
];

export const TherapistPortal = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const { toast } = useToast();
  
  const [sessions] = useState<Session[]>(mockSessions);
  const [isOnlineAvailable, setIsOnlineAvailable] = useState(false);
  const [therapistProfile, setTherapistProfile] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  const stats: TherapistStats = {
    totalEarnings: 2450,
    upcomingSessions: sessions.filter(s => s.status === 'scheduled').length,
    completedSessions: 18,
    averageRating: 4.8,
    totalClients: 12
  };

  useEffect(() => {
    fetchTherapistProfile();
  }, [user]);

  const fetchTherapistProfile = async () => {
    if (!user) return;
    
    try {
      const { data, error } = await supabase
        .from('therapists')
        .select('*')
        .eq('id', user.id)
        .maybeSingle();

      if (error) {
        console.error('Error fetching therapist profile:', error);
        toast({
          title: "Error",
          description: "No se pudo cargar el perfil del terapeuta",
          variant: "destructive",
        });
        return;
      }

      if (!data) {
        // Therapist profile doesn't exist
        toast({
          title: "Perfil incompleto",
          description: "Por favor completa tu perfil de terapeuta para acceder al portal",
          variant: "destructive",
        });
        return;
      }

      setTherapistProfile(data);
      setIsOnlineAvailable(data?.is_online_available || false);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleOnlineAvailability = async () => {
    if (!user || !therapistProfile) return;

    const newStatus = !isOnlineAvailable;
    
    try {
      const { error } = await supabase
        .from('therapists')
        .update({ is_online_available: newStatus })
        .eq('id', user.id);

      if (error) {
        toast({
          title: "Error",
          description: "No se pudo actualizar la disponibilidad",
          variant: "destructive",
        });
        return;
      }

      setIsOnlineAvailable(newStatus);
      toast({
        title: "Disponibilidad actualizada",
        description: newStatus 
          ? "Ahora estás disponible para sesiones inmediatas" 
          : "Ya no estás disponible para sesiones inmediatas",
      });
    } catch (error) {
      console.error('Error updating availability:', error);
      toast({
        title: "Error",
        description: "Ocurrió un error inesperado",
        variant: "destructive",
      });
    }
  };

  const upcomingSessions = sessions.filter(s => s.status === 'scheduled');
  const todaySessions = upcomingSessions.filter(s => 
    new Date(s.scheduled_at).toDateString() === new Date().toDateString()
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-background/50 p-6">
        <div className="mx-auto max-w-7xl">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
            <p className="mt-4 text-muted-foreground">Cargando portal...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-background/50 p-6">
      <div className="mx-auto max-w-7xl">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">
                {t('therapist.portal.welcome')} Dr. {user?.user_metadata?.first_name}
              </h1>
              <p className="text-muted-foreground">
                {t('therapist.portal.subtitle')}
              </p>
            </div>
            
            {/* Quick Actions */}
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <span className="text-sm font-medium">
                  {t('therapist.availability.immediate')}
                </span>
                <Switch
                  checked={isOnlineAvailable}
                  onCheckedChange={toggleOnlineAvailability}
                />
              </div>
              <Button>
                {t('therapist.actions.updateProfile')}
              </Button>
            </div>
          </div>
        </div>

        {/* Availability Status */}
        {isOnlineAvailable && (
          <div className="mb-6">
            <Card className="border-primary/20 bg-primary/5">
              <CardContent className="p-4">
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                  <p className="text-sm font-medium text-primary">
                    {t('therapist.status.availableNow')}
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-8">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">
                    {t('therapist.stats.earnings')}
                  </p>
                  <p className="text-2xl font-bold text-primary">${stats.totalEarnings}</p>
                </div>
                <DollarSign className="h-8 w-8 text-primary" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">
                    {t('therapist.stats.upcoming')}
                  </p>
                  <p className="text-2xl font-bold text-primary">{stats.upcomingSessions}</p>
                </div>
                <Calendar className="h-8 w-8 text-primary" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">
                    {t('therapist.stats.completed')}
                  </p>
                  <p className="text-2xl font-bold text-primary">{stats.completedSessions}</p>
                </div>
                <Clock className="h-8 w-8 text-primary" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">
                    {t('therapist.stats.rating')}
                  </p>
                  <p className="text-2xl font-bold text-primary">{stats.averageRating}</p>
                </div>
                <TrendingUp className="h-8 w-8 text-primary" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">
                    {t('therapist.stats.clients')}
                  </p>
                  <p className="text-2xl font-bold text-primary">{stats.totalClients}</p>
                </div>
                <Users className="h-8 w-8 text-primary" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content */}
        <Tabs defaultValue="requests" className="space-y-6">
          <TabsList className="grid w-full grid-cols-6">
            <TabsTrigger value="requests">Requests</TabsTrigger>
            <TabsTrigger value="sessions">{t('therapist.tabs.sessions')}</TabsTrigger>
            <TabsTrigger value="calendar">{t('therapist.tabs.calendar')}</TabsTrigger>
            <TabsTrigger value="clients">{t('therapist.tabs.clients')}</TabsTrigger>
            <TabsTrigger value="earnings">{t('therapist.tabs.earnings')}</TabsTrigger>
            <TabsTrigger value="profile">{t('therapist.tabs.profile')}</TabsTrigger>
          </TabsList>

          <TabsContent value="requests">
            <SessionRequestsTab />
          </TabsContent>

          <TabsContent value="sessions" className="space-y-6">
            {/* Today's Sessions */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="h-5 w-5" />
                  {t('therapist.sessions.today')}
                </CardTitle>
                <CardDescription>
                  {t('therapist.sessions.todayDesc')}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {todaySessions.length > 0 ? (
                  <div className="space-y-4">
                    {todaySessions.map((session) => (
                      <div key={session.id} className="flex items-center justify-between p-4 border rounded-lg">
                        <div className="flex items-center space-x-4">
                          <div className="flex items-center justify-center w-12 h-12 bg-primary/10 rounded-full">
                            <Users className="h-6 w-6 text-primary" />
                          </div>
                          <div>
                            <h3 className="font-semibold">{session.client_name}</h3>
                            <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                              <Clock className="h-4 w-4" />
                              <span>{new Date(session.scheduled_at).toLocaleTimeString()}</span>
                              <Badge variant="outline">{session.session_type}</Badge>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center space-x-3">
                          <span className="font-semibold">${session.price}</span>
                          <Button size="sm">{t('therapist.sessions.start')}</Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Calendar className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">{t('therapist.sessions.noToday')}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Upcoming Sessions */}
            <Card>
              <CardHeader>
                <CardTitle>{t('therapist.sessions.upcoming')}</CardTitle>
                <CardDescription>
                  {t('therapist.sessions.upcomingDesc')}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {upcomingSessions.length > 0 ? (
                  <div className="space-y-4">
                    {upcomingSessions.slice(0, 5).map((session) => (
                      <div key={session.id} className="flex items-center justify-between p-4 border rounded-lg">
                        <div className="flex items-center space-x-4">
                          <div className="flex items-center justify-center w-12 h-12 bg-secondary/20 rounded-full">
                            <Users className="h-6 w-6 text-secondary" />
                          </div>
                          <div>
                            <h3 className="font-semibold">{session.client_name}</h3>
                            <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                              <Clock className="h-4 w-4" />
                              <span>{new Date(session.scheduled_at).toLocaleString()}</span>
                              <Badge variant="outline">{session.session_type}</Badge>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center space-x-3">
                          <span className="font-semibold">${session.price}</span>
                          <Button variant="outline" size="sm">{t('therapist.sessions.reschedule')}</Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Calendar className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">{t('therapist.sessions.noUpcoming')}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="calendar">
            <CalendarTab />
          </TabsContent>

          <TabsContent value="clients">
            <ClientsTab />
          </TabsContent>

          <TabsContent value="earnings">
            <EarningsTab />
          </TabsContent>

          <TabsContent value="profile">
            <Card>
              <CardHeader>
                <CardTitle>{t('therapist.profile.title')}</CardTitle>
                <CardDescription>
                  {t('therapist.profile.desc')}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-center py-8">
                  <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">{t('therapist.profile.comingSoon')}</p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};